import { Component, Input, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, TemplateRef } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, FormControl } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { OptionService } from 'src/app/services/api/option.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { TextAreaFieldComponent } from 'src/app/components/form/textarea-field/textarea-field.component';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { FormNotesCustomersComponent } from 'src/app/modules/customers/components/notes-customers-form/notes-customers-form.component';
import { LoaderService } from 'src/app/services/loader.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessControlService } from 'src/app/services/access-control.service';

@Component({
  selector: 'projects-issue',
  imports: [
    ReactiveFormsModule,
    SelectFieldComponent,
    InputFieldComponent,
    TextAreaFieldComponent,
    FormNotesCustomersComponent
],
  templateUrl: './projects-issue.component.html',
  styleUrl: './projects-issue.component.scss'
})
export class ProjectsIssueComponent implements OnInit, OnDestroy {
  @Input() projectId?: string;
  @Input() readonly: boolean = false;
  @ViewChild('notesModalContent') notesModalContent?: TemplateRef<any>;

  projectIssues: any[] = [];
  issuePriorityColors: Map<number, string> = new Map();
  issueFormControls: Map<number, {
    priority: FormControl;
    testName: FormControl;
    jurisdiction: FormControl;
    workingListName: FormControl;
    status: FormControl;
    tester: FormControl;
    step: FormControl;
    description: FormControl;
    verified: FormControl;
  }> = new Map();

  changedIssues: Map<number, any> = new Map();

  columns: Array<{
    key: string;
    label: string;
    width: string;
    type: 'checkbox' | 'text' | 'select' | 'display';
    sortable?: boolean;
    align?: 'center' | 'left' | 'right';
  }> = [
    { key: 'verified', label: 'Verified', width: '60px', type: 'checkbox', align: 'center' },
    { key: 'priority', label: 'Priority', width: '1fr', type: 'select', align: 'center' },
    { key: 'jurisdiction', label: 'Jurisdiction', width: '1fr', type: 'text', align: 'center' },
    { key: 'workingListName', label: 'Working List Name', width: '1fr', type: 'text', align: 'center' },
    { key: 'testName', label: 'Test Name', width: '1fr', type: 'text', align: 'center' },
    { key: 'status', label: 'Status', width: '1fr', type: 'select', align: 'center' },
    { key: 'tester', label: 'Tester', width: '1fr', type: 'text', align: 'center' },
    { key: 'actions', label: '', width: '100px', type: 'display', align: 'center' }
  ];

  get gridTemplateColumns(): string {
    return this.columns.map(col => col.width).join(' ');
  }

  priorityFilterControl = new FormControl('');
  jurisdictionFilterControl = new FormControl('');
  workingListFilterControl = new FormControl('');
  statusFilterControl = new FormControl('');
  testNameFilterControl = new FormControl('');

  priorityOptions: { key: any; value: string }[] = [];
  statusOptions: { key: any; value: string }[] = [];
  
  issuePriorityOptions: { key: any; value: string }[] = [];
  issueStatusOptions: { key: any; value: string }[] = [];
  
  jurisdictionOptions: { key: any; value: string }[] = [];
  workingListOptions: { key: any; value: string }[] = [];
  testNameOptions: { key: any; value: string }[] = [];

  legendOptions = [
    { color: '#dc3545', label: 'Critical block', type: 'critical-block' },
    { color: '#ffc107', label: 'Critical non block', type: 'critical-non-block' },
    { color: '#17a2b8', label: 'Non critical non block', type: 'non-critical-non-block' }
  ];

  issueToDelete: any = null;
  modalRef?: NgbModalRef;
  selectedIssueForNotes: any = null;
  issueNotes: any[] = [];
  canDeleteIssue = true;
  canEditVerifiedCheckbox = true;
  canEditStatus = true;
  canEditPriority = true;

  private destroy$ = new Subject<void>();

  constructor(
    public projectsService: ProjectsService,
    private optionService: OptionService,
    private alertNotificationService: AlertNotificationService,
    private loaderService: LoaderService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal,
    private confirmationDialogService: ConfirmationDialogService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {}

  ngOnInit(): void {
    this.loadProjectIssues();
    this.loadIssuePrioritiesAndStatuses();
    this.subscribeToFilterChanges();

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canDeleteIssue = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_ISSUES_DELETE', 'delete'
        );
        this.canEditVerifiedCheckbox = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_ISSUES_VERIFIED_CHECKBOX', 'modify'
        );
        this.canEditStatus = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_ISSUES_STATUS_EDIT', 'modify'
        );
        this.canEditPriority = this.accessControlService.hasFeatureActionAccess(
          profileCode, 'PROJECTS_ISSUES_PRIORITY_EDIT', 'modify'
        );
        this.updateVerifiedControlsDisabledState();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadIssuePrioritiesAndStatuses(): void {
    this.optionService.projectIssuePriorities()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.issuePriorityOptions = response?.data?.options || [];
        },
        error: (error) => console.error('Error loading issue priorities:', error)
      });

    this.optionService.projectIssueStatuses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.issueStatusOptions = response?.data?.options || [];
        },
        error: (error) => console.error('Error loading issue statuses:', error)
      });
  }

  loadProjectIssues(): void {
    if (!this.projectId) return;

    this.loaderService.startLoading();
    const projectIdNum = this.projectId;

    this.projectsService.listProjectIssues(Number(projectIdNum))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.projectIssues = response?.data?.projectIssues ;
          console.log(response.data);
          this.initializeFormControls();
          this.loadFilterOptions();
          this.loaderService.stopLoading();
        },
        error: (error) => {
          console.error('Error loading project issues:', error);
          this.alertNotificationService.error('Error loading project issues');
          this.loaderService.stopLoading();
        }
      });
  }

  initializeFormControls(): void {
    this.issueFormControls.clear();
    this.issuePriorityColors.clear();
    this.changedIssues.clear();
    this.projectIssues.forEach(issue => {
      const verifiedControl = new FormControl({
        value: issue.verifiedByPm || false,
        disabled: this.readonly || !this.canEditVerifiedCheckbox
      });
      this.issueFormControls.set(issue.projectIssueId, {
        priority: new FormControl(issue.priority || ''),
        testName: new FormControl(issue.projectTestlistStep.projectTestlist.testlist.name || ''),
        jurisdiction: new FormControl(issue.projectTestlistStep.projectTestlist.projectChecklist.projectWorkinglist.projectItem.proposalItem.jurisdiction.name || ''),
        workingListName: new FormControl(issue.projectTestlistStep.projectTestlist.projectChecklist.projectWorkinglist.name || ''),
        status: new FormControl(issue.status || ''),
        tester: new FormControl(issue.creatorUser.firstName + ' ' + issue.creatorUser.lastName || ''),
        step: new FormControl(issue.projectTestlistStep.projectTestlist.testlist.name || ''),
        description: new FormControl(issue.description || ''),
        verified: verifiedControl
      });
      const color = this.getPriorityColor(issue.priority);
      this.issuePriorityColors.set(issue.projectIssueId, color);
    });
  }

  getIssueControls(issueId: number) {
    if (!this.issueFormControls.has(issueId)) {
      this.issueFormControls.set(issueId, {
        priority: new FormControl(''),
        testName: new FormControl(''),
        jurisdiction: new FormControl(''),
        workingListName: new FormControl(''),
        status: new FormControl(''),
        tester: new FormControl(''),
        step: new FormControl(''),
        description: new FormControl(''),
        verified: new FormControl({ value: false, disabled: this.readonly || !this.canEditVerifiedCheckbox })
      });
      if (!this.issuePriorityColors.has(issueId)) {
        this.issuePriorityColors.set(issueId, '#17a2b8');
      }
    }
    return this.issueFormControls.get(issueId)!;
  }

  private updateVerifiedControlsDisabledState(): void {
    const shouldDisable = this.readonly || !this.canEditVerifiedCheckbox;
    this.issueFormControls.forEach(controls => {
      if (shouldDisable) {
        controls.verified.disable({ emitEvent: false });
      } else {
        controls.verified.enable({ emitEvent: false });
      }
    });
  }

  getColumnWidth(columnKey: string): string {
    const column = this.columns.find(c => c.key === columnKey);
    return column ? column.width : '1fr';
  }

  loadFilterOptions(): void {
    if (!this.projectId || this.projectIssues.length === 0) return;

    const projectIdNum = this.projectId;
    const emptyOption = { key: '', value: '' };

    this.optionService.projectIssueFilterPriority(Number(projectIdNum))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.priorityOptions = [emptyOption, ...(response?.data?.options || [])];
        },
        error: (error) => console.error('Error loading priorities:', error)
      });

    this.optionService.projectIssueFilterJurisdictions(Number(projectIdNum))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.jurisdictionOptions = [emptyOption, ...(response?.data?.options || [])];
        },
        error: (error) => console.error('Error loading issue jurisdictions:', error)
      });

    this.optionService.projectIssueFilterWorkingListNames(Number(projectIdNum))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.workingListOptions = [emptyOption, ...(response?.data?.options || [])];
        },
        error: (error) => console.error('Error loading working lists:', error)
      });

    this.optionService.projectIssueFilterTestName(Number(projectIdNum))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.testNameOptions = [emptyOption, ...(response?.data?.options || [])];
        },
        error: (error) => console.error('Error loading test names:', error)
      });

    this.optionService.projectIssueFilterStatus(Number(projectIdNum))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.statusOptions = [emptyOption, ...(response?.data?.options || [])];
        },
        error: (error) => console.error('Error loading statuses:', error)
      });
  }

  subscribeToFilterChanges(): void {
    this.priorityFilterControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.applyFilters();
      });

    this.jurisdictionFilterControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.applyFilters();
      });

    this.workingListFilterControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.applyFilters();
      });

    this.statusFilterControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.applyFilters();
      });

    this.testNameFilterControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.applyFilters();
      });
  }

  applyFilters(): void {
    if (!this.projectId) return;

    const projectIdNum = this.projectId;

    this.loaderService.startLoading();


    let queryParams = new HttpParams();
    
    
    if (this.priorityFilterControl.value) {
      queryParams = queryParams.set('priority', this.priorityFilterControl.value);
    }
    if (this.jurisdictionFilterControl.value) {
      queryParams = queryParams.set('jurisdictionId', this.jurisdictionFilterControl.value);
    }
    if (this.workingListFilterControl.value) {
      queryParams = queryParams.set('workinglistName', this.workingListFilterControl.value);
    }
    if (this.statusFilterControl.value) {
      queryParams = queryParams.set('status', this.statusFilterControl.value);
    }
    if (this.testNameFilterControl.value) {
      queryParams = queryParams.set('testlistName', this.testNameFilterControl.value);
    }


    this.projectsService.listProjectIssues(Number(projectIdNum), { params: queryParams })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.projectIssues = response?.data?.projectIssues || [];
          this.initializeFormControls();
          this.loaderService.stopLoading();
        },
        error: (error) => {
          console.error('Error applying filters:', error);
          this.alertNotificationService.error('Error applying filters');
          this.loaderService.stopLoading();
        }
      });
  }

  onPriorityChange(issue: any, newPriority: any): void {
    if (this.readonly || !this.canEditPriority) return;


    issue.priority = newPriority;
    const newColor = this.getPriorityColor(newPriority);
    this.issuePriorityColors.set(issue.projectIssueId, newColor);
    this.getIssueControls(issue.projectIssueId).priority.setValue(newPriority, { emitEvent: false });

    if (!this.changedIssues.has(issue.projectIssueId)) {
      this.changedIssues.set(issue.projectIssueId, { ...issue });
    } else {
      this.changedIssues.get(issue.projectIssueId).priority = newPriority;
    }
  }

  onTestNameChange(issue: any, newTestName: any): void {
    if (this.readonly) return;


    issue.testName = newTestName;
    this.getIssueControls(issue.projectIssueId).testName.setValue(newTestName, { emitEvent: false });

    if (!this.changedIssues.has(issue.projectIssueId)) {
      this.changedIssues.set(issue.projectIssueId, { ...issue });
    } else {
      this.changedIssues.get(issue.projectIssueId).testName = newTestName;
    }
  }

  onStatusChange(issue: any, newStatus: any): void {
    if (this.readonly || !this.canEditStatus) return;

    issue.status = newStatus;
    this.getIssueControls(issue.projectIssueId).status.setValue(newStatus, { emitEvent: false });

    if (!this.changedIssues.has(issue.projectIssueId)) {
      this.changedIssues.set(issue.projectIssueId, { ...issue });
    } else {
      this.changedIssues.get(issue.projectIssueId).status = newStatus;
    }
  }

  onVerifiedChange(issue: any, event: any): void {
    if (this.readonly || !this.canEditVerifiedCheckbox) return;

    const isVerified = event.target?.checked || false;
    
    issue.verifiedByPm = isVerified;
    this.getIssueControls(issue.projectIssueId).verified.setValue(isVerified, { emitEvent: false });

    if (!this.changedIssues.has(issue.projectIssueId)) {
      this.changedIssues.set(issue.projectIssueId, { ...issue });
    } else {
      this.changedIssues.get(issue.projectIssueId).verifiedByPm = isVerified;
    }
  }

  confirmDelete(issue: any): void {
    this.issueToDelete = issue;
    this.confirmationDialogService.open(
      'Are you sure you want to delete this issue?',
      () => this.deleteIssue()
    );
  }

  deleteIssue(): void {
    if (!this.issueToDelete || !this.projectId) return;

    const projectIdNum = this.projectId;

    this.loaderService.startLoading();
    this.projectsService.deleteProjectIssue(Number(projectIdNum), this.issueToDelete.projectIssueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.projectIssues = this.projectIssues.filter(
            issue => issue.projectIssueId !== this.issueToDelete.projectIssueId
          );
          this.issueFormControls.delete(this.issueToDelete.projectIssueId);
          this.issuePriorityColors.delete(this.issueToDelete.projectIssueId);
          this.issueToDelete = null;
          this.alertNotificationService.success('Issue deleted successfully');
          this.loaderService.stopLoading();
        },
        error: (error) => {
          console.error('Error deleting issue:', error);
          this.alertNotificationService.error('Error deleting issue');
          this.issueToDelete = null;
          this.loaderService.stopLoading();
        }
      });
  }

  openNotesModal(issue: any): void {
    this.selectedIssueForNotes = issue;
    
    if (!this.projectId) return;
    
    this.loaderService.startLoading();
    this.projectsService.listProjectIssuesNotes(Number(this.projectId), issue.projectIssueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.issueNotes = response?.data?.sectionNotes || [];
          this.loaderService.stopLoading();
          
          if (this.notesModalContent) {
            this.modalRef = this.modalService.open(this.notesModalContent, { 
              size: 'lg',
              centered: true,
              backdrop: 'static'
            });
            this.modalRef.result.then(
              () => {
                this.selectedIssueForNotes = null;
              },
              () => {
                this.selectedIssueForNotes = null;
              }
            );
          }
        },
        error: (error) => {
          console.error('Error loading issue notes:', error);
          this.alertNotificationService.error('Error loading issue notes');
          this.loaderService.stopLoading();
        }
      });
  }

  closeNotesModal(): void {
    if (this.modalRef) {
      this.modalRef.close();
    }
    this.selectedIssueForNotes = null;
  }

  downloadAttachment(issue: any): void {
    if (!this.projectId || !issue.projectIssueId || !issue.attachments || issue.attachments.length === 0) return;
    
    this.loaderService.startLoading();
    if (issue.attachments) {
      this.loaderService.stopLoading();
      this.projectsService.GET(issue.attachments[0].url).subscribe({
        next: (response: any) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        },
        error: (error: any) => {
          this.alertNotificationService.error('Error downloading file');
          console.error('Error:', error);
        }
      });
    }
  }

  getIssueColorClass(issue: any): string {
    if (issue.priority === 'CRITICAL_BLOCK') {
      return 'issue-critical-block';
    } else if (issue.priority === 'CRITICAL_NOT_BLOCK') {
      return 'issue-critical-non-block';
    } else {
      return 'issue-non-critical-non-block';
    }
  }

  getPriorityColor(priority: string): string {
    if (priority === 'CRITICAL_BLOCK') {
      return '#dc3545';
    } else if (priority === 'CRITICAL_NOT_BLOCK') {
      return '#ffc107';
    } else {
      return '#17a2b8';
    }
  }

  getIssueBorderColor(issue: any): string {
    return this.issuePriorityColors.get(issue.projectIssueId) || this.getPriorityColor(issue.priority);
  }

  submitForm(): void {
    if (this.changedIssues.size === 0) {
      this.alertNotificationService.success('No changes to save');
      return;
    }

    if (!this.projectId) return;

    this.loaderService.startLoading();
    const projectIdNum = this.projectId;
    let updateCount = 0;
    let errorCount = 0;

    this.changedIssues.forEach((changedIssue, issueId) => {
      const updateData = {
        status: changedIssue.status,
        priority: changedIssue.priority,
        verifiedByPm: Boolean(changedIssue.verifiedByPm) || false
      };

      this.projectsService.updateProjectIssue(Number(projectIdNum), issueId, updateData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            updateCount++;
            if (updateCount + errorCount === this.changedIssues.size) {
              this.loaderService.stopLoading();
              this.changedIssues.clear();
              if (errorCount === 0) {
                this.alertNotificationService.success(`All ${updateCount} issue(s) updated successfully`);
              } else {
                this.alertNotificationService.error(`Updated ${updateCount} issue(s) with ${errorCount} error(s)`);
              }
            }
          },
          error: (error) => {
            errorCount++;
            console.error('Error updating issue:', error);
            if (updateCount + errorCount === this.changedIssues.size) {
              this.loaderService.stopLoading();
              if (errorCount === 0) {
                this.alertNotificationService.success(`All ${updateCount} issue(s) updated successfully`);
              } else {
                this.alertNotificationService.error(`Updated ${updateCount} issue(s) with ${errorCount} error(s)`);
              }
            }
          }
        });
    });
  }

  exportExcelIssues(): void {
    if (!this.projectId) return;

    this.loaderService.startLoading();
    const projectIdNum = this.projectId;

    this.projectsService.getProjectIssuesExcel(Number(projectIdNum))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loaderService.stopLoading();

          if (response?.data?.base64) {
            const base64Data = response.data.base64;
            const filename = response?.data?.fileName || 'project-issues.xlsx';

            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();

            window.URL.revokeObjectURL(url);
            this.alertNotificationService.success('File Excel downloaded successfully');
          } else {
            console.warn('Base64 data not found in response', response);
            this.alertNotificationService.error('Unable to download file: missing base64 data');
          }
        },
        error: (error) => {
          this.loaderService.stopLoading();
          console.error('Error downloading Excel file:', error);
          this.alertNotificationService.error('Error downloading Excel file');
        }
      });
  }

}
